import React, { useState } from 'react';
import { CANVAS_BACKGROUNDS, CONNECTOR_STYLES, FONT_SIZE_POINTS, ARROW_SIZE_OPTIONS } from '../../constants/canvasSettings';

// Canvas appearance dialog: connector style, background color, node shading.
// Opened from the canvas right-click menu.

const sectionLabel = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-secondary, #5F6368)',
  margin: '0 0 6px',
  fontWeight: 500,
};

const CanvasAppearanceModal = ({ settings, onChange, onApplyConnectorStyleToAll, onClose }) => {
  const [connectorStyle, setConnectorStyle] = useState(settings.defaultEdgeType);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--color-bg-panel, #fff)',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          width: 420,
          maxWidth: '92vw',
          padding: '18px 20px',
          fontFamily: 'var(--font-family-primary, inherit)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary, #202124)' }}>
            Canvas appearance
          </h3>
          <button
            onClick={onClose}
            title="Close"
            style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-secondary, #5F6368)' }}
          >×</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <p style={sectionLabel}>Connector style</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={connectorStyle}
              onChange={(e) => {
                setConnectorStyle(e.target.value);
                onChange({ defaultEdgeType: e.target.value });
              }}
              style={{ flex: 1, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-medium, #DADCE0)' }}
            >
              {CONNECTOR_STYLES.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <button
              className="btn-small"
              title="Restyle every connector in this survey, including ones with their own style"
              onClick={() => onApplyConnectorStyleToAll(connectorStyle)}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid var(--color-border-medium, #DADCE0)', background: '#fff', cursor: 'pointer' }}
            >
              Apply to all
            </button>
          </div>
          <small style={{ color: 'var(--color-text-tertiary, #888)', fontSize: 12 }}>
            New connectors use this style. Connectors given their own style in the Edge panel keep it unless you apply to all.
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <p style={sectionLabel}>Arrowheads</p>
          <select
            value={settings.arrowSize}
            onChange={(e) => onChange({ arrowSize: Number(e.target.value) })}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-medium, #DADCE0)' }}
          >
            {ARROW_SIZE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <small style={{ color: 'var(--color-text-tertiary, #888)', fontSize: 12 }}>
            Direction-of-flow arrows at each connector's end. Choose None to hide them.
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <p style={sectionLabel}>Background</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {CANVAS_BACKGROUNDS.map(bg => {
              const selected = settings.background === bg.color;
              return (
                <button
                  key={bg.id}
                  title={bg.label}
                  onClick={() => onChange({ background: bg.color })}
                  style={{
                    width: 30, height: 30, borderRadius: 6, cursor: 'pointer',
                    background: bg.color,
                    border: selected
                      ? '2px solid var(--color-primary-600, #3D85E6)'
                      : '1px solid var(--color-border-medium, #DADCE0)',
                    boxShadow: selected ? '0 0 0 2px var(--color-primary-100, #D1E7FF)' : 'none',
                  }}
                />
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <p style={sectionLabel}>Text sizes</p>
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { key: 'nodeTitlePt', label: 'Node title' },
              { key: 'nodeTextPt', label: 'Node text' },
              { key: 'edgeTextPt', label: 'Connector labels' },
            ].map(({ key, label }) => (
              <label key={key} style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary, #5F6368)' }}>
                {label}
                <select
                  value={settings[key]}
                  onChange={(e) => onChange({ [key]: Number(e.target.value) })}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-medium, #DADCE0)' }}
                >
                  {FONT_SIZE_POINTS.map(pt => (
                    <option key={pt} value={pt}>{pt} pt</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: 'var(--color-text-primary, #202124)' }}>
            <input
              type="checkbox"
              checked={!!settings.shadeByType}
              onChange={(e) => onChange({ shadeByType: e.target.checked })}
            />
            Shade nodes by type
          </label>
          <small style={{ color: 'var(--color-text-tertiary, #888)', fontSize: 12, marginLeft: 24, display: 'block' }}>
            Questions, decisions, packages, and media each get a pale tint so types stand apart at a glance.
          </small>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            onClick={onClose}
            style={{ padding: '6px 16px', borderRadius: 4, border: '1px solid var(--color-border-medium, #DADCE0)', background: '#fff', cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CanvasAppearanceModal;
